/**
 * Frontend auth katmanı — token localStorage'da tutulur, window.fetch patch'i
 * TÜM /api isteklerine (generated client + sayfalardaki raw fetch'ler) Bearer ekler.
 */
const TOKEN_KEY = "geolop:token";
const USER_KEY = "geolop:user";

export type AuthUser = { id: number; email: string; name: string };

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  try { return JSON.parse(localStorage.getItem(USER_KEY) ?? "null"); } catch { return null; }
}

// Hesaba özel sayfa hafızası — hesap değişince taşınmaması gerekenler.
// CANLI VAKA (2026-08-28, capan2007): aynı tarayıcıda önce başka hesapla oturum
// açılmıştı; "aktif site" hafızası (activeDomainId) yeni kullanıcıya taşındı,
// Google bağlama isteği ESKİ hesabın site id'siyle gitti ve sahiplik koruması
// 404 döndürdü — kullanıcı Google'ı hiç bağlayamadı.
const HESABA_OZEL_ANAHTARLAR = ["geolop:activeDomainId"];
function hesabaOzelTemizle() {
  for (const k of HESABA_OZEL_ANAHTARLAR) { try { localStorage.removeItem(k); } catch { /* yoksay */ } }
}

export function setAuth(token: string, user: AuthUser) {
  const onceki = getStoredUser();
  if (!onceki || onceki.id !== user.id) hesabaOzelTemizle(); // farklı hesap girişi
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  hesabaOzelTemizle();
}

export async function logout() {
  try { await fetch("/api/auth/logout", { method: "POST" }); } catch { /* offline olsa da çıkış yapılır */ }
  clearAuth();
  window.location.href = import.meta.env.BASE_URL || "/";
}

/**
 * Google login dönüşü: backend callback'i /#gtoken=...&guser=... ile yönlendirir.
 * Render'dan ÖNCE çağrılır — token'ı saklar ve URL'i temizler.
 */
export function captureGoogleRedirect() {
  const hash = window.location.hash;
  const t = hash.match(/gtoken=([^&]+)/);
  if (!t) return;
  const u = hash.match(/guser=([^&]+)/);
  let user: AuthUser = { id: 0, email: "", name: "" };
  try { if (u) user = JSON.parse(decodeURIComponent(u[1])); } catch { /* kullanıcı bilgisi opsiyonel */ }
  setAuth(t[1], user);
  history.replaceState(null, "", window.location.pathname + window.location.search);
}

/** main.tsx'te render'dan ÖNCE bir kez çağrılır. */
export function installAuthFetch() {
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    // JETON SIZINTISI KORUMASI: eski kontrol `url.includes("/api/")` idi — bu, mutlak bir
    // dış adresle de eşleşiyordu (ör. https://baskasite.example/api/x) ve oturum jetonunu
    // üçüncü tarafa Authorization başlığıyla gönderiyordu. Artık YALNIZ kendi kökenimizdeki
    // /api yolu jeton alır.
    let isApi = false;
    try {
      const abs = new URL(url, window.location.origin);
      isApi = abs.origin === window.location.origin && abs.pathname.startsWith("/api/");
    } catch { isApi = false; }
    if (!isApi) return original(input, init);

    const token = getToken();
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (token && !headers.has("authorization")) headers.set("authorization", `Bearer ${token}`);

    const res = await original(input, { ...init, headers });

    // Oturum düşmüşse (token silinmiş/geçersiz) → login ekranına dön.
    // /api/auth/* hariç: login denemesindeki 401 "parola hatalı" demek, oturum kaybı değil.
    if (res.status === 401 && !url.includes("/api/auth/") && getToken()) {
      clearAuth();
      window.location.reload();
    }
    return res;
  };
}
